// src/exercises/useLiveTranscription.js
// ══════════════════════════════════════════════════════════════════════════════
// 🎙️ EL OÍDO EN VIVO — el alumno ve lo que dice mientras lo dice.
//
// Antes: pulsaba el micro, hablaba a ciegas, soltaba, y el texto aparecía de
// golpe unos segundos después. Si el micro no cogía bien o se comía media
// frase, se enteraba al final, cuando ya no había nada que hacer.
//
// Ahora el audio va en trozos de 250 ms a Deepgram por WebSocket y el texto se
// va escribiendo solo. Al soltar, el alumno tiene el texto delante para
// corregirlo antes de mandárselo a Spacy.
//
// ── LO QUE NO SE ROMPE ────────────────────────────────────────────────────────
// El camino de siempre (grabar entero → subir → /api/tutor/transcribe) sigue
// intacto y es la red de seguridad. Si el WebSocket no abre, se cae, o el
// navegador no lo soporta, el hook devuelve `fallbackBlob` con el audio completo
// y quien llama lo sube como toda la vida. El alumno no se entera de nada.
//
// Por eso se graba SIEMPRE en local aunque el streaming vaya bien: cuesta
// memoria (unos pocos cientos de KB) y compra que un fallo a mitad de frase no
// se lleve por delante lo que el alumno acababa de decir.
//
// ── NO SE PAGA DOS VECES ──────────────────────────────────────────────────────
// Deepgram factura minutos de audio, entren como entren. El texto final sale del
// mismo stream que pintaba los parciales, así que cuando el WebSocket funciona
// NO se llama a /transcribe. Se sustituye, no se suma.
// ══════════════════════════════════════════════════════════════════════════════

import { useCallback, useRef, useState } from 'react';

const CHUNK_MS = 250;          // cada cuánto se manda audio
const CIERRE_MS = 3500;        // margen para que Deepgram vacíe su buffer al soltar

// Códigos que manda el backend al cerrar. Distinguen "no se pudo" de "no debes".
const WS_UNAUTHORIZED = 4001;
const WS_FORBIDDEN = 4003;
const WS_POLICY = 4008;

// ── DE DÓNDE SALE LA URL DEL SOCKET ───────────────────────────────────────────
// El resto de la app llama a `/api/...` a secas y deja que el proxy lo resuelva:
// en desarrollo lo reenvía Vite, en producción lo reescribe Vercel hacia Render.
//
// Con un WebSocket eso NO vale. Las reescrituras de vercel.json no negocian el
// upgrade de protocolo: la conexión se queda en una respuesta HTTP normal y el
// socket nunca abre. Así que aquí se apunta al backend directamente, usando
// VITE_API_URL, que es la misma variable que ya usa todo lo demás.
//
// Si VITE_API_URL no está puesta se cae al mismo host, que funciona en
// desarrollo (Vite sí hace de proxy de WebSockets, `ws: true` en vite.config).
// En producción sin esa variable el socket no abrirá — y no pasa nada: el hook
// devuelve el audio grabado y se sube por /api/tutor/transcribe como siempre.
// El alumno no nota la diferencia salvo que deja de ver el texto en vivo.
//
// Se devuelven las DOS candidatas y se prueban en cascada, porque cuál funciona
// depende del entorno y adivinarlo desde el código es imposible:
//
//   · El backend directo (VITE_API_URL) es lo único que sirve en producción.
//   · El mismo origen sirve en desarrollo, donde Vite hace de proxy, y es lo
//     único que hay si VITE_API_URL no llegó al bundle.
//
// Probarlas en secuencia —nunca a la vez, que serían dos conexiones a Deepgram
// y dos facturas— cuesta cero cuando la primera funciona.
function urlsDelSocket() {
    const token = localStorage.getItem('token') || '';
    const cola = `/api/tutor/listen-live?token=${encodeURIComponent(token)}`;
    const base = import.meta.env.VITE_API_URL || '';

    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const mismoOrigen = `${protocolo}//${window.location.host}${cola}`;

    if (!/^https?:\/\//i.test(base)) return [mismoOrigen];

    const directo = base.replace(/^http/i, 'ws').replace(/\/+$/, '') + cola;
    return directo === mismoOrigen ? [directo] : [directo, mismoOrigen];
}

// Se anota por qué no se pudo, para que un fallo deje de ser una caja de texto
// vacía sin explicación. `motivo` lo lee la UI; el detalle va a la consola.
function describirCierre(codigo) {
    if (codigo === WS_UNAUTHORIZED) return 'sesión no válida';
    if (codigo === WS_FORBIDDEN) return 'plan sin acceso';
    if (codigo === WS_POLICY) return 'grabación demasiado larga';
    return 'sin conexión en vivo';
}

// Abre la primera URL que responda. Resuelve con el socket abierto, o con null.
function abrirSocket(urls) {
    return new Promise((resolve) => {
        let i = 0;

        const intentar = () => {
            if (i >= urls.length) {
                console.warn('[oído en vivo] ninguna URL abrió; se usará el oído por lotes');
                resolve(null);
                return;
            }
            const url = urls[i++];
            let resuelto = false;
            let ws;
            try {
                ws = new WebSocket(url);
            } catch (e) {
                console.warn('[oído en vivo] no se pudo crear el socket:', url, e);
                intentar();
                return;
            }
            ws.binaryType = 'arraybuffer';

            // Si el servidor no contesta, no se puede esperar indefinidamente:
            // el alumno ya está hablando.
            const plazo = setTimeout(() => {
                if (resuelto) return;
                resuelto = true;
                console.warn('[oído en vivo] tiempo agotado al conectar:', url);
                try { ws.close(); } catch { /* ya cerrado */ }
                intentar();
            }, 2500);

            ws.onopen = () => {
                if (resuelto) return;
                resuelto = true;
                clearTimeout(plazo);
                console.info('[oído en vivo] conectado:', url.replace(/token=[^&]*/, 'token=***'));
                resolve(ws);
            };

            ws.onclose = (ev) => {
                if (resuelto) return;
                resuelto = true;
                clearTimeout(plazo);
                console.warn(`[oído en vivo] cerró antes de abrir (código ${ev.code}) en ${url.replace(/token=[^&]*/, 'token=***')}`);
                intentar();
            };

            ws.onerror = () => { /* el cierre que viene detrás es quien decide */ };
        };

        intentar();
    });
}

// Manda un trozo. El Blob va TAL CUAL, sin convertir: `.arrayBuffer()` devuelve
// una promesa y trozos consecutivos pueden resolver desordenados, con lo que
// Deepgram recibiría un WebM descolocado y no devolvería nada en absoluto.
// `send()` es síncrono y encola en el orden en que se llama.
function enviarTrozo(ws, blob) {
    try {
        ws.send(blob);
    } catch (e) {
        // Que falle un envío no puede ser silencioso: sin esto, el síntoma es
        // una caja de texto que no se llena y ninguna pista de por qué.
        console.warn('[oído en vivo] no se pudo enviar un trozo:', e);
    }
}


export default function useLiveTranscription() {
    const [isRecording, setIsRecording] = useState(false);
    const [liveText, setLiveText] = useState('');
    const [isLive, setIsLive] = useState(false);   // ¿el streaming llegó a funcionar?
    const [motivoSinVivo, setMotivoSinVivo] = useState('');   // por qué no hay vivo

    // Espejo síncrono de `isLive`. `stop()` se llama a veces en el mismo ciclo
    // en que el socket acaba de abrir o de caerse, y el valor de estado todavía
    // sería el viejo: se esperaría un texto que no va a llegar, o se descartaría
    // uno que sí. El ref siempre dice la verdad en el instante de leerlo.
    const isLiveRef = useRef(false);

    const wsRef = useRef(null);
    const grabadorRef = useRef(null);
    const streamRef = useRef(null);
    const trozosRef = useRef([]);
    // Trozos grabados ANTES de que el socket estuviera listo. El primero lleva
    // la cabecera del WebM; perderlo deja el stream indescifrable.
    const colaRef = useRef([]);
    const cierreRef = useRef(null);                // promesa que resuelve al soltar
    const acumuladoRef = useRef('');
    const parcialRef = useRef('');

    const limpiar = useCallback(() => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ya cerrado */ }
        streamRef.current = null;
        grabadorRef.current = null;
        colaRef.current = [];
        try { wsRef.current?.close(); } catch { /* ya cerrado */ }
        wsRef.current = null;
    }, []);

    // ── ARRANCAR ──────────────────────────────────────────────────────────────
    const start = useCallback(async () => {
        setLiveText('');
        acumuladoRef.current = '';
        parcialRef.current = '';
        trozosRef.current = [];
        colaRef.current = [];
        setIsLive(false);
        isLiveRef.current = false;
        setMotivoSinVivo('');

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const grabador = new MediaRecorder(stream);
        grabadorRef.current = grabador;

        // ⚠️ EL PRIMER TROZO NO SE PUEDE PERDER.
        //
        // MediaRecorder mete la cabecera del contenedor WebM en el PRIMER
        // fragmento; los demás son continuación y por sí solos no significan
        // nada. La versión anterior arrancaba la grabación y el socket a la vez,
        // y descartaba todo trozo que llegara antes de que el socket estuviera
        // abierto:
        //
        //     if (ws && ws.readyState === WebSocket.OPEN) ws.send(...)
        //
        // Basta con que la conexión tarde más de 250 ms —el primer trozo— para
        // que Deepgram reciba un WebM sin cabecera. Y sin cabecera no da un
        // texto peor: no da NINGUNO. Es exactamente el síntoma de "el socket
        // está abierto y no aparece ni una palabra".
        //
        // Ahora los trozos se guardan en una cola desde el primer milisegundo y
        // se sueltan en orden en cuanto hay conexión. La grabación no espera a
        // nadie y la cabecera siempre llega la primera.
        grabador.ondataavailable = (evento) => {
            if (!evento.data || evento.data.size === 0) return;

            // Siempre en local: es la red de seguridad del oído por lotes.
            trozosRef.current.push(evento.data);

            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) {
                enviarTrozo(ws, evento.data);
            } else {
                // Todavía no hay socket (o ya se cayó). Se guarda.
                colaRef.current.push(evento.data);
            }
        };

        // La grabación arranca YA, sin esperar al socket. Si esperara, los
        // primeros segundos —donde más se duda y más falta hace ver el texto—
        // se perderían mientras se negocia la conexión.
        grabador.start(CHUNK_MS);
        setIsRecording(true);

        // El socket se abre en paralelo.
        abrirSocket(urlsDelSocket()).then((ws) => {
            if (!ws) {
                setMotivoSinVivo('sin conexión en vivo');
                return;
            }
            // El alumno pudo soltar el botón mientras se conectaba.
            if (!grabadorRef.current) {
                try { ws.close(); } catch { /* ya cerrado */ }
                return;
            }

            wsRef.current = ws;

            ws.onmessage = (evento) => {
                let dato;
                try { dato = JSON.parse(evento.data); } catch { return; }

                if (dato.tipo === 'cierre') {
                    cierreRef.current?.resolver({
                        texto: dato.texto || acumuladoRef.current,
                        metricas: dato.metricas || null,
                    });
                    return;
                }

                // El acumulado son las frases ya confirmadas; el parcial es la
                // hipótesis en curso, que se corrige sola. Pintar los dos juntos
                // es lo que da la sensación de que el texto se escribe solo.
                if (dato.tipo === 'final') {
                    acumuladoRef.current = dato.acumulado || acumuladoRef.current;
                    parcialRef.current = '';
                } else {
                    parcialRef.current = dato.texto || '';
                }
                setLiveText(`${acumuladoRef.current} ${parcialRef.current}`.trim());
            };

            ws.onclose = (evento) => {
                isLiveRef.current = false;
                setIsLive(false);
                if (evento.code && evento.code !== 1000) {
                    setMotivoSinVivo(describirCierre(evento.code));
                    console.warn(`[oído en vivo] socket cerrado con código ${evento.code}`);
                }
                // Si se cierra con el alumno esperando el texto, se desbloquea
                // para que caiga al oído por lotes en vez de quedarse colgado.
                if (cierreRef.current) {
                    cierreRef.current.resolver({
                        texto: acumuladoRef.current,
                        metricas: null,
                        motivo: evento.code,
                    });
                }
            };

            // Y ahora sí, la cola: en orden y empezando por la cabecera.
            const pendientes = colaRef.current;
            colaRef.current = [];
            for (const trozo of pendientes) enviarTrozo(ws, trozo);

            isLiveRef.current = true;
            setIsLive(true);
        });
    }, []);

    // ── SOLTAR ────────────────────────────────────────────────────────────────
    // Devuelve { texto, metricas, fallbackBlob }.
    //   - Con `texto`, quien llama ya tiene lo que dijo el alumno.
    //   - Con `fallbackBlob` y sin `texto`, hay que subirlo a /transcribe.
    const stop = useCallback(async () => {
        setIsRecording(false);

        const grabador = grabadorRef.current;
        if (grabador && grabador.state !== 'inactive') {
            // `requestData` fuerza a soltar el último trozo antes de parar. Sin
            // esto se pierden hasta 250 ms de audio, que suele ser la última
            // palabra — la que peor se pronuncia y más interesa ver.
            try { grabador.requestData(); } catch { /* algunos navegadores no lo tienen */ }
            grabador.stop();
        }

        const ws = wsRef.current;
        const huboStreaming = isLiveRef.current && ws && ws.readyState === WebSocket.OPEN;

        let resultado = { texto: '', metricas: null };
        if (huboStreaming) {
            const espera = new Promise((resolve) => {
                cierreRef.current = { resolver: resolve };
                setTimeout(() => resolve({ texto: acumuladoRef.current, metricas: null }), CIERRE_MS);
            });
            try { ws.send('stop'); } catch { /* ya cerrado */ }
            resultado = await espera;
            cierreRef.current = null;
        }

        const blob = trozosRef.current.length
            ? new Blob(trozosRef.current, { type: 'audio/webm' })
            : null;

        limpiar();
        setLiveText('');

        const texto = (resultado.texto || '').trim();
        return {
            texto,
            metricas: resultado.metricas || null,
            // Sólo se ofrece el audio para subir si el streaming NO dio texto.
            // Subirlo teniendo ya el transcrito sería pagar el mismo audio dos
            // veces por exactamente el mismo resultado.
            fallbackBlob: texto ? null : blob,
            motivo: resultado.motivo || null,
        };
    }, [limpiar]);

    const cancel = useCallback(() => {
        setIsRecording(false);
        setLiveText('');
        const grabador = grabadorRef.current;
        if (grabador && grabador.state !== 'inactive') {
            try { grabador.stop(); } catch { /* ya parado */ }
        }
        limpiar();
    }, [limpiar]);

    return { isRecording, liveText, isLive, motivoSinVivo, start, stop, cancel };
}
